import React, { useState } from "react";
import { useAuth } from "../../hooks/auth";
import { usePermissions } from "../../contexts/AuthPermissionsContext";
import {
  Box,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Image,
  Link,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { NavLink, Link as RouterLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import {
  AiOutlineAppstore,
  AiOutlineCalendar,
  AiOutlineDown,
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineLineChart,
  AiOutlineBarChart,
  AiOutlinePartition,
  AiOutlineContacts,
  AiOutlineSchedule,
  AiOutlineTrophy,
  AiOutlineTeam,
  AiOutlineFileSearch,
  AiOutlineSetting,
  AiOutlineCheckCircle,
  AiOutlineDollarCircle,
  AiOutlineFileText,
} from "react-icons/ai";

const logoUrl =
  "https://s3-contel-imagens-aplicacao.s3.us-east-1.amazonaws.com/nogle/Brand.svg";

const COMERCIAL_PREFIX = "/comercial";

// ─── Variantes de Animação dos Ícones e Textos ──────────────────────────────

const mainIconVariants = {
  initial: { scale: 1, rotate: 0, y: 0 },
  hover: {
    scale: 1.25,
    rotate: 6,
    transition: { type: "spring", stiffness: 450, damping: 14 },
  },
  tap: { scale: 0.92 },
};

const settingIconVariants = {
  initial: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.25,
    rotate: 45,
    transition: { type: "spring", stiffness: 400, damping: 15 },
  },
  tap: { scale: 0.92 },
};

const subIconVariants = {
  initial: { scale: 1, x: 0, rotate: 0 },
  hover: {
    scale: 1.22,
    x: 3,
    rotate: -4,
    transition: { type: "spring", stiffness: 450, damping: 14 },
  },
  tap: { scale: 0.92 },
};

const textShiftVariants = {
  initial: { x: 0 },
  hover: { x: 2.5, transition: { type: "spring", stiffness: 400, damping: 18 } },
};

// ─── Componentes de Item de Navegação ────────────────────────────────────────

const NavItem = ({ icon: IconComponent, children, isCollapsed, to = "#", ...rest }) => {
  const labelText = typeof children === "string" ? children : (children?.props?.children || "");
  const iconVar = IconComponent === AiOutlineSetting ? settingIconVariants : mainIconVariants;

  return (
    <Tooltip
      label={labelText}
      isDisabled={!isCollapsed}
      placement="right"
      hasArrow
      borderRadius="md"
      bg="surface"
      color="textPrimary"
      fontSize="xs"
      fontWeight="700"
      px={3}
      py={1.5}
      boxShadow="dark-lg"
    >
      <Link
        as={NavLink}
        to={to}
        end
        _hover={{ textDecoration: "none" }}
        w="full"
        _activeLink={{ color: "secondary" }}
      >
        {({ isActive }) => (
          <Flex
            as={motion.div}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            align="center"
            h="44px"
            borderRadius="xl"
            cursor="pointer"
            w="full"
            px={isCollapsed ? "18px" : 3.5}
            position="relative"
            bg={isActive ? "secondarySubtle" : "transparent"}
            color={isActive ? "secondary" : "sidebarText"}
            fontWeight={isActive ? "800" : "600"}
            _hover={{ bg: "whiteAlpha.100", color: "white" }}
            transition="background 0.2s ease, color 0.2s ease, padding 0.3s ease"
            overflow="hidden"
            {...rest}
          >
            {/* Indicador de item ativo */}
            <Box
              position="absolute"
              left="0"
              h="60%"
              w="3.5px"
              bg="secondary"
              borderRightRadius="full"
              opacity={isActive ? 1 : 0}
              transition="opacity 0.2s ease-in-out"
              top="50%"
              transform="translateY(-50%)"
              boxShadow={isActive ? "0 0 10px rgba(1, 233, 117, 0.7)" : "none"}
            />

            {/* Ícone animado via prop de variação da linha inteira */}
            {IconComponent && (
              <Flex w="36px" h="36px" align="center" justify="center" flexShrink={0}>
                <motion.div
                  variants={iconVar}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    filter: isActive ? "drop-shadow(0 0 6px rgba(1, 233, 117, 0.6))" : "none",
                  }}
                >
                  <Icon fontSize="20px" as={IconComponent} />
                </motion.div>
              </Flex>
            )}

            {/* Texto do rótulo com transição suave */}
            <motion.div
              initial={false}
              animate={{
                opacity: isCollapsed ? 0 : 1,
                width: isCollapsed ? 0 : "auto",
                marginLeft: isCollapsed ? 0 : "0.5rem",
              }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                flex: 1,
              }}
            >
              <motion.div variants={textShiftVariants}>
                {children}
              </motion.div>
            </motion.div>
          </Flex>
        )}
      </Link>
    </Tooltip>
  );
};

const SubNavItem = ({ icon: IconComponent, children, to = "#" }) => {
  const iconVar = IconComponent === AiOutlineSetting ? settingIconVariants : subIconVariants;

  return (
    <Link
      as={NavLink}
      to={to}
      end
      _hover={{ textDecoration: "none" }}
      w="full"
      _activeLink={{ color: "secondary" }}
    >
      {({ isActive }) => (
        <Flex
          as={motion.div}
          initial="initial"
          whileHover="hover"
          whileTap="tap"
          align="center"
          h="38px"
          borderRadius="lg"
          cursor="pointer"
          transition="background 0.2s ease, color 0.2s ease"
          _hover={{ bg: "whiteAlpha.100", color: "white" }}
          w="full"
          px={3}
          position="relative"
          bg={isActive ? "secondarySubtle" : "transparent"}
          color={isActive ? "secondary" : "sidebarText"}
          whiteSpace="nowrap"
        >
          {/* Ponto indicador ativo */}
          <Box
            position="absolute"
            left="-15px"
            h="6px"
            w="6px"
            borderRadius="full"
            bg="secondary"
            opacity={isActive ? 1 : 0}
            transition="opacity 0.2s ease, transform 0.2s ease"
            transform={isActive ? "translateY(-50%) scale(1)" : "translateY(-50%) scale(0)"}
            top="50%"
            boxShadow="0 0 8px #01E975"
          />

          {/* Ícone do sub-item animado no hover do botão */}
          {IconComponent && (
            <motion.div
              variants={iconVar}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                filter: isActive ? "drop-shadow(0 0 5px rgba(1, 233, 117, 0.6))" : "none",
              }}
            >
              <Icon fontSize="15px" as={IconComponent} />
            </motion.div>
          )}

          <motion.div variants={textShiftVariants} style={{ marginLeft: "0.625rem" }}>
            <Text fontSize="xs" fontWeight={isActive ? "750" : "550"}>
              {children}
            </Text>
          </motion.div>
        </Flex>
      )}
    </Link>
  );
};

const GroupToggle = ({ icon: IconComponent, children, isCollapsed, isOpen, isActive, onClick }) => {
  const labelText = typeof children === "string" ? children : (children?.props?.children || "");
  const iconVar = IconComponent === AiOutlineSetting ? settingIconVariants : mainIconVariants;

  return (
    <Tooltip
      label={labelText}
      isDisabled={!isCollapsed}
      placement="right"
      hasArrow
      borderRadius="md"
      bg="surface"
      color="textPrimary"
      fontSize="xs"
      fontWeight="700"
      px={3}
      py={1.5}
      boxShadow="dark-lg"
    >
      <Flex
        as={motion.div}
        initial="initial"
        whileHover="hover"
        whileTap="tap"
        align="center"
        h="44px"
        borderRadius="xl"
        cursor="pointer"
        w="full"
        px={isCollapsed ? "18px" : 3.5}
        position="relative"
        color={isActive ? "secondary" : "sidebarText"}
        bg={isActive ? "whiteAlpha.50" : "transparent"}
        mb={1}
        _hover={{ bg: "whiteAlpha.100", color: "white" }}
        transition="background 0.2s ease, color 0.2s ease, padding 0.3s ease"
        overflow="hidden"
        onClick={onClick}
      >
        {/* Ícone do grupo animado */}
        {IconComponent && (
          <Flex w="36px" h="36px" align="center" justify="center" flexShrink={0}>
            <motion.div
              variants={iconVar}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                filter: isActive ? "drop-shadow(0 0 6px rgba(1, 233, 117, 0.5))" : "none",
              }}
            >
              <Icon fontSize="20px" as={IconComponent} />
            </motion.div>
          </Flex>
        )}

        {/* Texto do grupo */}
        <motion.div
          initial={false}
          animate={{
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : "auto",
            marginLeft: isCollapsed ? 0 : "0.5rem",
          }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            flex: 1,
          }}
        >
          <motion.div variants={textShiftVariants}>
            {children}
          </motion.div>
        </motion.div>

        {/* Chevron com rotação animada */}
        <motion.div
          initial={false}
          animate={{
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : "auto",
          }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          style={{ overflow: "hidden" }}
        >
          <Flex
            w="22px"
            h="22px"
            align="center"
            justify="center"
            borderRadius="full"
            bg={isOpen ? "secondarySubtle" : "whiteAlpha.100"}
            transition="background 0.2s ease"
          >
            <motion.div
              animate={{ rotate: isOpen ? 0 : -90 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Icon
                as={AiOutlineDown}
                fontSize="10px"
                color={isOpen ? "secondary" : "sidebarText"}
              />
            </motion.div>
          </Flex>
        </motion.div>
      </Flex>
    </Tooltip>
  );
};

const Sidebar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { role, temPermissao } = usePermissions();

  const isComercialPath = location.pathname.startsWith(COMERCIAL_PREFIX);

  const CONTROLES_PATHS = ["/usuarios", "/controladores"];
  const isControlesPath = CONTROLES_PATHS.includes(location.pathname);

  const [comercialOpen, setComercialOpen] = useState(isComercialPath);
  const [controlesOpen, setControlesOpen] = useState(isControlesPath);

  return (
    <Box
      as={motion.div}
      animate={{ width: isCollapsed ? "80px" : "280px" }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      overflowX="hidden"
      bg="sidebarBg"
      borderRight="1px"
      borderColor="sidebarBorder"
      boxShadow="none"
      h="100vh"
      zIndex={10}
      flexShrink={0}
      display="flex"
      flexDirection="column"
    >
      {/* Cabeçalho: Logo / Botão de Alternância */}
      <Flex
        h="20"
        alignItems="center"
        px={isCollapsed ? 3 : 5}
        justifyContent={isCollapsed ? "center" : "space-between"}
        flexShrink={0}
        position="relative"
        transition="padding 0.3s ease"
      >
        <motion.div
          initial={false}
          animate={{
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : "100px",
          }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          style={{ overflow: "hidden", whiteSpace: "nowrap" }}
        >
          <RouterLink to="/">
            <Image src={logoUrl} alt="Nogle Logo" w="100px" cursor="pointer" minW="100px" />
          </RouterLink>
        </motion.div>

        <IconButton
          aria-label="Toggle Menu"
          icon={
            <motion.div
              animate={{ rotate: isCollapsed ? 0 : 180 }}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Icon as={isCollapsed ? AiOutlineMenu : AiOutlineClose} fontSize="xl" />
            </motion.div>
          }
          variant="ghost"
          onClick={onToggle}
          _hover={{ bg: "whiteAlpha.100", color: "secondary" }}
          color="secondary"
          borderRadius="xl"
          w="40px"
          h="40px"
          minW="40px"
        />
      </Flex>

      {/* Navegação */}
      <VStack
        spacing={1}
        align="stretch"
        as="nav"
        px={isCollapsed ? 2 : 4}
        overflowY="auto"
        flex={1}
        sx={{
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-thumb': { bg: 'sidebarBorder', borderRadius: 'full' },
        }}
      >
        {/* Dashboard (admin-only) */}
        {role === 'admin' && (
          <NavItem icon={AiOutlineAppstore} isCollapsed={isCollapsed} to="/">
            <Text>Dashboard</Text>
          </NavItem>
        )}

        {/* ── COMERCIAL ── */}
        {temPermissao('comercial') && (
          <>
            <GroupToggle
              icon={AiOutlineLineChart}
              isCollapsed={isCollapsed}
              isOpen={comercialOpen}
              isActive={isComercialPath}
              onClick={() => setComercialOpen(v => !v)}
            >
              <Text fontWeight="700">Comercial</Text>
            </GroupToggle>

            <Collapse in={comercialOpen && !isCollapsed} animateOpacity>
              <VStack spacing={1} align="stretch" ml={5} pl={3} borderLeft="1px solid" borderColor="sidebarBorder" my={1}>
                <SubNavItem icon={AiOutlineBarChart} to="/comercial">Dashboard</SubNavItem>
                <SubNavItem icon={AiOutlinePartition} to="/comercial/pipeline">Pipeline</SubNavItem>
                <SubNavItem icon={AiOutlineContacts} to="/comercial/contatos">Contatos</SubNavItem>
                <SubNavItem icon={AiOutlineDollarCircle} to="/comercial/negocios">Negócios</SubNavItem>
                <SubNavItem icon={AiOutlineSchedule} to="/comercial/atividades">Atividades</SubNavItem>
                <SubNavItem icon={AiOutlineFileText} to="/comercial/anotacoes">Anotações</SubNavItem>
                <SubNavItem icon={AiOutlineTrophy} to="/comercial/metas">Metas</SubNavItem>
                <SubNavItem icon={AiOutlineCheckCircle} to="/comercial/concluidas">Relatórios</SubNavItem>
              </VStack>
            </Collapse>
          </>
        )}

        {/* Calendário (standalone) */}
        <NavItem icon={AiOutlineCalendar} isCollapsed={isCollapsed} to="/calendario">
          <Text>Calendário</Text>
        </NavItem>

        {/* ── CONTROLES (admin-only) ── */}
        {role === 'admin' && (
          <>
            <GroupToggle
              icon={AiOutlineSetting}
              isCollapsed={isCollapsed}
              isOpen={controlesOpen}
              isActive={isControlesPath}
              onClick={() => setControlesOpen(v => !v)}
            >
              <Text fontWeight="700">Controles</Text>
            </GroupToggle>

            <Collapse in={controlesOpen && !isCollapsed} animateOpacity>
              <VStack spacing={1} align="stretch" ml={5} pl={3} borderLeft="1px solid" borderColor="sidebarBorder" my={1}>
                <SubNavItem icon={AiOutlineTeam} to="/usuarios">Usuários</SubNavItem>
                <SubNavItem icon={AiOutlineSetting} to="/controladores">Controladores</SubNavItem>
              </VStack>
            </Collapse>
          </>
        )}

        {/* Consulta Marca (standalone) */}
        <NavItem icon={AiOutlineFileSearch} isCollapsed={isCollapsed} to="/consulta-marca">
          <Text>Consulta Marca</Text>
        </NavItem>
      </VStack>
    </Box>
  );
};

export default Sidebar;
